import React, {useEffect, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import DialogContainer, {DialogButton,} from '../../AlertDialog/DialogContainer';
import {useDispatch} from 'react-redux';

import {Formik} from 'formik';
import SettingsForm from '../../Forms/AutoAttendantsMenus/SettingsForm';
import {settingsFormValidationSchema} from '../../Forms/AutoAttendantsMenus/SettingsForm.utils';
import PromptForm from '../../Forms/AutoAttendantsMenus/PromptForm';
import {promptsFormValidationSchema} from '../../Forms/AutoAttendantsMenus/PromptForm.utils';
import {defaultValues} from '../../../hooks/useEditAutoAttendantMenuFormData';
import Tabs from '../../Tabs/Tabs';
import {useStyles} from './utils';
import classNames from 'classnames';
import PermissionProvider from '../../PermissionProvider/PermissionProvider';
import {intervalFormValidationSchema} from "../../IntervalSelect/IntervalSelect.utils";
import {EditAutoAttendantsMenuForm, isDirty} from "../../../views/AutoAttendants/AutoAttendantsMenuDetails.utils";
import ErrorNotification from "../../TabErrors/ErrorNotification";
import {actions} from "../../../store";
import {indexOf} from "lodash";
import {getTabNumber} from "../../../utils/getTabNumber";

interface EditMenuDialogProps {
    isOpen: boolean;
    menu?: EditAutoAttendantsMenuForm;
    toggleVisibility?: () => void;
    onSave?: (form: EditAutoAttendantsMenuForm) => void;
    menuSettingsPermission: string[];
    menuPromptsPermission: string[];
}

const formValidationSchema = settingsFormValidationSchema
    .concat(promptsFormValidationSchema)
    .concat(intervalFormValidationSchema);

const EditMenuDialog: React.VFC<EditMenuDialogProps> = (
    {
        menu,
        isOpen,
        toggleVisibility,
        onSave,
        menuSettingsPermission,
        menuPromptsPermission
    }) => {
    const {t} = useTranslation();
    const classes = useStyles();
    const dispatch = useDispatch();


    const [tabIndex, setTabIndex] = useState(getTabNumber([0, 1]));
    const [saveClicked, setSaveClicked] = useState(false);

    const lastMenuRef = useRef(menu);
    if (menu) lastMenuRef.current = menu;
    const currentMenu = menu ?? lastMenuRef.current;

    const tabNames =
        [
            t('common:settings'),
            t('screens:autoAttendants.prompts'),
        ]

    const formsErrorsValidationFuncRef = useRef<
        { [key in string]: (() => boolean) | undefined }
    >({
        settings: undefined,
        prompts: undefined
    });


    const setFormIsValidFunc = (
        funcName: keyof typeof formsErrorsValidationFuncRef.current,
        func: () => boolean,
    ) => {
        formsErrorsValidationFuncRef.current[funcName] = func;
    };

    const markTabAsHaveError = (tabName: string, tabIndex: number, setTabError: boolean) => {
        dispatch(actions.setErrorInTab({name: tabName, index: tabIndex, markAsInvalid: setTabError}))
    }

    const tabsNameResolver = {
        settings: t('common:settings'),
        prompts: t('screens:autoAttendants.prompts')
    };

    const areAllFormsValid = () => {
        let formsAreValid = true;
        Object.keys(formsErrorsValidationFuncRef.current).forEach((key) => {
            let setTabError;
            if (
                formsErrorsValidationFuncRef.current[key] !== undefined &&
                !formsErrorsValidationFuncRef.current[key]?.()
            ) {
                formsAreValid = false;
                setTabError = true;
            } else {
                setTabError = false
            }

            // @ts-ignore
            markTabAsHaveError(tabsNameResolver[key], tabNames.indexOf(tabsNameResolver[key]), setTabError);

        });
        return formsAreValid;
    };

    useEffect(() => {
        isOpen && areAllFormsValid();
    }, [isOpen]);

    const onTabChange = (_: React.ChangeEvent<{}>, index: any) => {
        dispatch(actions.saveForms({visible: false}))
        setSaveClicked(false);
        areAllFormsValid();
        setTabIndex(index);
    }

    return (
        <Formik<EditAutoAttendantsMenuForm>
            initialValues={currentMenu || defaultValues}
            validationSchema={formValidationSchema}
            onSubmit={(form) => {
                onSave?.(form);
            }}
            enableReinitialize
            validateOnChange={false}
        >
            {({submitForm, dirty, resetForm, values, initialValues}) => (
                <>
                    <DialogContainer
                        contentClass={classes.content}
                        isOpen={isOpen}
                        dataQa="add-new-menu-dialog"
                        dataTestId="add-new-menu-dialog"
                        header={t('screens:autoAttendants.editNamedMenu', {
                            value: currentMenu?.name,
                        })}
                        dialogActionsButtons={[
                            <DialogButton
                                key="cancel"
                                label={t('common:cancel')}
                                onClick={() => {
                                    toggleVisibility?.();
                                    setSaveClicked(false);
                                    resetForm();
                                }}
                                dataQa={'add-new-menu-dialog-cancel-button'}
                            />,
                            <DialogButton
                                className={classNames(
                                    !dirty && classes.disabledButton,
                                )}
                                disabled={!isDirty(dirty, values, initialValues)}
                                key="save"
                                label={t('common:save')}
                                onClick={() => {
                                    setSaveClicked(true);
                                    submitForm()
                                }}
                                dataQa={'add-new-menu-dialog-save-button'}
                                primary
                            />,
                        ]}
                    >
                        <Tabs
                            tabsNames={[
                                t('common:settings'),
                                t('screens:autoAttendants.prompts'),
                            ]}
                            className={classes.tabs}
                            onChange={onTabChange}
                        >
                            <PermissionProvider
                                permission={
                                    menuSettingsPermission
                                }
                            >
                                <SettingsForm handleSetIsValidFunc={setFormIsValidFunc} callback={() => {
                                    markTabAsHaveError(tabsNameResolver['settings'], indexOf(tabNames, tabsNameResolver['settings']), true);
                                    dispatch(actions.saveForms({visible: true}));
                                }}
                                customTitle={t('screens:addToRingGroup.active')}/>
                            </PermissionProvider>

                            <PermissionProvider
                                permission={
                                    menuPromptsPermission
                                }
                            >
                                <PromptForm handleSetIsValidFunc={setFormIsValidFunc} callback={() => {
                                    markTabAsHaveError(tabsNameResolver['prompts'], indexOf(tabNames, tabsNameResolver['prompts']), true);
                                    dispatch(actions.saveForms({ visible: true }));
                                }} menuId={currentMenu?.menuId}/>
                            </PermissionProvider>
                        </Tabs>
                        <ErrorNotification tabName={tabNames[tabIndex]} isSubmitting={saveClicked}/>
                    </DialogContainer>
                </>
            )}
        </Formik>
    );
};

export default EditMenuDialog;
